/**
 * useAuth Hook
 * ฮุคหลักสำหรับการยืนยันตัวตน
 *
 * Main auth hook providing auth state and actions.
 * ฮุคหลักสำหรับ auth ให้ state และ actions
 */

import { useCallback } from 'react'
import { useAuthStore } from './auth.store'
import { authApi } from './auth.api'
import { hasPermission, hasAnyPermission, hasAllPermissions } from '@/shared/logic/acl.logic'
import { getRedirectPath } from './auth.logic'
import type { LoginInput } from './types'
import type { Permission } from '@/shared/types/permissions'

export const useAuth = () => {
  const {
    accessToken,
    user,
    isLoading,
    login: storeLogin,
    logout: storeLogout,
    setLoading,
  } = useAuthStore()
  const isAuthenticated = !!accessToken && !!user

  /**
   * Login with credentials.
   * เข้าสู่ระบบด้วยข้อมูลรับรอง
   *
   * @returns redirect path after login
   */
  const login = useCallback(
    async (input: LoginInput): Promise<string> => {
      setLoading(true)
      try {
        const response = await authApi.login(input)
        storeLogin(response.accessToken, response.user)
        return getRedirectPath(response.user.role)
      } catch (error) {
        setLoading(false)
        throw error
      }
    },
    [storeLogin, setLoading]
  )

  /**
   * Logout the current user.
   * ออกจากระบบผู้ใช้ปัจจุบัน
   */
  const logout = useCallback(async () => {
    try {
      await authApi.logout()
    } catch {
      // Ignore logout API errors — still clear local state
    } finally {
      storeLogout()
    }
  }, [storeLogout])

  /**
   * Refresh access token using HTTP-only cookie.
   * รีเฟรช access token โดยใช้ HTTP-only cookie
   */
  const refresh = useCallback(async () => {
    try {
      const response = await authApi.refreshToken()
      storeLogin(response.accessToken, response.user)
      return true
    } catch {
      storeLogout()
      return false
    }
  }, [storeLogin, storeLogout])

  // Permission helpers
  const checkPermission = useCallback(
    (permission: Permission) =>
      user?.permissions ? hasPermission(user.permissions, permission) : false,
    [user?.permissions]
  )

  const checkAnyPermission = useCallback(
    (permissions: Permission[]) =>
      user?.permissions ? hasAnyPermission(user.permissions, permissions) : false,
    [user?.permissions]
  )

  const checkAllPermissions = useCallback(
    (permissions: Permission[]) =>
      user?.permissions ? hasAllPermissions(user.permissions, permissions) : false,
    [user?.permissions]
  )

  return {
    // State
    user,
    isAuthenticated,
    isLoading,

    // Actions
    login,
    logout,
    refresh,

    // Permission helpers
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
  }
}
